import React, { useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig';
import { ThemeProvider } from './contexts/ThemeContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import HomeScreen from './screens/HomeScreen';
import AuthScreen from './screens/AuthScreen';
import DashboardScreen from './screens/DashboardScreen';
import SettingsScreen from './screens/SettingsScreen';
import ProposalWizardScreen from './screens/ProposalWizardScreen';
import ContractGeneratorScreen from './screens/ContractGeneratorScreen';
import ProfileScreen from './screens/ProfileScreen';
import AdminScreen from './screens/AdminScreen';
import TaskManagerScreen from './screens/TaskManagerScreen';
import CaseStudyScreen from './screens/CaseStudyScreen';
import OrganizationScreen from './screens/OrganizationScreen';

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeScreen, setActiveScreen] = useState('dashboard'); // Default to dashboard for logged-in users
  const [showAuth, setShowAuth] = useState(false); // Toggle between home and auth when not logged in
  const [forceLanding, setForceLanding] = useState(false); // Force show landing page

  // Debug: Log when active screen changes
  useEffect(() => {
    console.log('📺 Active screen changed to:', activeScreen);
  }, [activeScreen]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Handle routing based on URL path (only on mount)
  useEffect(() => {
    const path = window.location.pathname;
    
    if (path === '/landing') {
      setForceLanding(true);
    } else if (path === '/dashboard' && user) {
      setActiveScreen('dashboard');
      setForceLanding(false);
    } else {
      setForceLanding(false);
    }
  }, [user]); // Removed activeScreen and forceLanding to prevent loops

  // Update URL when screen changes
  useEffect(() => {
    if (activeScreen && user && !forceLanding) {
      // Update URL to match current screen
      const path = activeScreen === 'wizard' ? '/proposal-generator' : activeScreen === 'contracts' ? '/contract-generator' : `/${activeScreen}`;
      window.history.pushState({}, '', path);
    }
  }, [activeScreen, user, forceLanding]);

  if (loading) {
    return null;
  }

  // Force show landing page at /landing regardless of auth status
  if (forceLanding || !user) {
    if (showAuth && !forceLanding) {
      return <AuthScreen showHome={true} onBackToHome={() => setShowAuth(false)} />;
    }
    return (
      <HomeScreen 
        onGetStarted={() => {
          setShowAuth(true);
          setForceLanding(false);
        }}
        onSignIn={() => {
          setShowAuth(true);
          setForceLanding(false);
        }}
      />
    );
  }

  // Render all screens but only show the active one
  // This way all screens maintain their state
  return (
    <View style={styles.container}>
      <View style={[styles.screen, activeScreen === 'dashboard' ? styles.activeScreen : styles.hiddenScreen]}>
        <DashboardScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'wizard' ? styles.activeScreen : styles.hiddenScreen]}>
        <ProposalWizardScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'contracts' ? styles.activeScreen : styles.hiddenScreen]}>
        <ContractGeneratorScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'settings' ? styles.activeScreen : styles.hiddenScreen]}>
        <SettingsScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen === 'ProposalWizard' ? 'wizard' : screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'profile' ? styles.activeScreen : styles.hiddenScreen]}>
        <ProfileScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'admin' ? styles.activeScreen : styles.hiddenScreen]}>
        <AdminScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'tasks' ? styles.activeScreen : styles.hiddenScreen]}>
        <TaskManagerScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'casestudy' ? styles.activeScreen : styles.hiddenScreen]}>
        <CaseStudyScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
      <View style={[styles.screen, activeScreen === 'organization' ? styles.activeScreen : styles.hiddenScreen]}>
        <OrganizationScreen 
          navigation={{ 
            navigate: (screen) => {
              console.log('🚀 Navigation called:', screen);
              setActiveScreen(screen.toLowerCase());
            }
          }} 
        />
      </View>
    </View>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <OrganizationProvider>
        <AppContent />
      </OrganizationProvider>
    </ThemeProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  screen: {
    flex: 1,
  },
  activeScreen: {
    display: 'flex',
  },
  hiddenScreen: {
    display: 'none',
  },
});